/**
 * CustomerHistory (CRM-001, completed 2026-06-11) — the everything-view: ONE
 * chronological activity feed merging every record type that touches the
 * customer. ServiceTitan / HubSpot / Salesforce all center the record on a
 * single timeline; ours assembles from the mirrors + Pegasus-native tables
 * we already have — no bespoke events table to drift.
 *
 * Sources: service visits (dispatches), invoices, messages (texts/emails/
 * logged calls), files, proposals (quote app), recommendations, follow-ups,
 * queue tickets, and the ownership handoff. Type chips filter; dispatches
 * PEEK in place (FLOW-01 — the working window never jumps).
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { deptLabel } from "@/lib/departments";
import {
  Wrench,
  Receipt,
  History as HistoryIcon,
  Home,
  ChevronRight,
  MessageSquare,
  FolderOpen,
  FileText,
  Lightbulb,
  BellRing,
  ListChecks,
  type LucideIcon,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { useCustomerDispatches, useCustomerSignals } from "./api";
import { useOwnerEra, PriorOwnerBadge } from "./ownerEra";
import { AllPropertiesChip } from "./ScopeChip";
import { useCustomerInvoices } from "@/features/invoice-detail/api";
import { useMessages } from "./CustomerMessages";
import { useCustomerProposals } from "./CustomerProposals";
import { useCustomerFiles, type PegasusFile } from "@/features/files/filesCore";
import { FilePreviewModal } from "@/features/files/FilePreviewModal";
import { useRecommendations } from "@/features/recommendations/recommendations";
import { extractJobPreview } from "@/lib/format";
import { DispatchPeek } from "./DispatchPeek";
import { InvoicePeek } from "./InvoicePeek";
import { useVisitEquipment, visitEquipmentFor, type VisitEquipment } from "./visitEquipment";
import { useCustomerWorkNotes, useVisitSummaries, summarizeVisit } from "./visitSummary";
import { VisitEquipmentBlock } from "./VisitEquipmentBlock";
// PROPERTY-LINK-01: the HOUSE's other accounts (builder / previous owner) and their visits.
import { useHouseVisitHistory, type HouseVisitBundle } from "@/features/mobile/field";
import { houseEraLabel, relationWord, paidByLabel, type HouseAccount } from "./houseHistory";

type Kind =
  | "service"
  | "invoice"
  | "message"
  | "file"
  | "proposal"
  | "recommendation"
  | "followup"
  | "ticket"
  | "ownership";

type Ev = {
  key: string;
  date: string; // ISO (date or datetime) — sorts lexicographically
  kind: Kind;
  /** Which property the record belongs to (null = account-wide, e.g. a text). */
  locno?: string | null;
  title: string;
  subtitle?: string | null;
  to?: string; // navigate (drill-downs)
  peekDispatch?: string; // peek in place (FLOW-01)
  fileId?: string; // file events open the preview modal (FILES-04)
  /** Shared dispatch — events carrying the same one CLUSTER into a visit
   *  block (Seth 2026-06-11: "block together the connected stuff"). */
  dispatch?: string | null;
  /** Dispatch (scheduling) notes — a short de-emphasized preview. */
  notes?: string | null;
  /** Set when this record predates the current owner — the prior owner's name. */
  prior?: string | null;
  /** Invoice work notes — the tech's "what was found / done" write-up, shown in
   *  full (Seth 2026-06-16: this is what actually happened on the record + what
   *  techs need in the field). Falls back to the dispatch-notes preview. */
  workNotes?: string | null;
  /** Invoice events PEEK the invoice in place (Seth 2026-06-16) instead of
   *  navigating; the peek has an "Open full invoice" link. */
  peekInvoiceId?: string;
  /** HIST-EQUIP-01: the unit(s) this visit installed / worked on (service rows). */
  equipment?: VisitEquipment | null;
};

/** A visit block (anchor service event + its invoice/files/messages) or a
 *  standalone event. */
type StreamItem =
  | { type: "block"; date: string; anchor: Ev; children: Ev[] }
  | { type: "single"; date: string; ev: Ev };

const money = (n: number | null) =>
  n == null
    ? ""
    : n.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      });

const META: Record<Kind, { icon: LucideIcon; tint: string; label: string }> = {
  service: { icon: Wrench, tint: "bg-blue-50 text-blue-600", label: "Service" },
  invoice: { icon: Receipt, tint: "bg-emerald-50 text-emerald-600", label: "Invoices" },
  message: { icon: MessageSquare, tint: "bg-brand-50 text-brand-600", label: "Messages" },
  file: { icon: FolderOpen, tint: "bg-slate-100 text-slate-500", label: "Files" },
  proposal: { icon: FileText, tint: "bg-sky-50 text-sky-600", label: "Quotes" },
  recommendation: { icon: Lightbulb, tint: "bg-rose-50 text-rose-600", label: "Recommendations" },
  followup: { icon: BellRing, tint: "bg-amber-50 text-amber-700", label: "Follow-ups" },
  ticket: { icon: ListChecks, tint: "bg-violet-50 text-violet-600", label: "Tickets" },
  ownership: { icon: Home, tint: "bg-amber-50 text-amber-600", label: "Ownership" },
};

const CHANNEL_WORD: Record<string, string> = {
  sms: "Text", email: "Email", chat: "Chat", web: "Web", call: "Call", note: "Note",
};


/** Completed is the NORM — only exceptions earn glance space. */
const isDoneStatus = (s: string | null | undefined) =>
  !!s && ["complete", "done"].includes(s.toLowerCase());

/** Pegasus-native follow-ups + queue tickets for the timeline (small,
 *  customer-scoped queries — inline here, the only consumer). */
function useTimelineExtras(custno: string) {
  return useQuery({
    queryKey: ["timeline_extras", custno],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const [fu, tix] = await Promise.all([
        supabase
          .from("pegasus_followup")
          .select("id, title, status, due_date, source, created_at")
          .eq("custno", custno)
          .order("created_at", { ascending: false })
          .limit(100),
        supabase
          .from("pegasus_dispatch")
          .select("id, summary, lane_key, status, source, created_at, resolved_at, resolved_by")
          .eq("custno", custno)
          .is("deleted_at", null)
          .order("created_at", { ascending: false })
          .limit(100),
      ]);
      // 360-AUDIT wave 2 (G): dropped errors rendered "no follow-ups / no
      // tickets" when the lookup failed. Throw so the query can retry/error.
      if (fu.error) throw fu.error;
      if (tix.error) throw tix.error;
      return {
        followups: (fu.data ?? []) as {
          id: string; title: string; status: string; due_date: string | null;
          source: string | null; created_at: string;
        }[],
        tickets: (tix.data ?? []) as {
          id: string; summary: string | null; lane_key: string | null; status: string | null;
          source: string | null; created_at: string; resolved_at: string | null; resolved_by: string | null;
        }[],
      };
    },
  });
}

/**
 * The INVOICE work notes per dispatch — the tech's actual write-up of what was
 * found / done (ESC stores it on the invoice's `M`/work lines; the `H`/HIST
 * lines are just the scheduling + call-log noise). This is "what happened on the
 * customer record" — the thing techs need in the field (Seth 2026-06-16), far
 * more useful than the dispatch's scheduling notes.
 */
const PAGE = 60;

export function CustomerHistory({
  custno,
  locno,
  customerName,
  multiProperty,
}: {
  custno: string;
  locno?: string | null;
  multiProperty?: boolean;
  /** The account's display name — labels the current-owner era chip when the
   *  transfer is note-derived (no recorded current-owner name). */
  customerName?: string | null;
}) {
  const { data: disp, isLoading: dispLoading } = useCustomerDispatches(custno);
  const { data: inv, isLoading: invLoading } = useCustomerInvoices(custno);
  const era = useOwnerEra(custno, locno);
  const { data: msgs } = useMessages(custno);
  const { data: files } = useCustomerFiles(custno);
  const { data: proposals } = useCustomerProposals(custno);
  const { data: recs } = useRecommendations(custno);
  const { data: extras } = useTimelineExtras(custno);
  const { data: workNotes } = useCustomerWorkNotes(custno);
  // HIST-EQUIP-01: which unit each visit installed / worked on.
  const { data: visitEq } = useVisitEquipment(custno);
  const summaryItems = useMemo(
    () =>
      (disp ?? [])
        .filter((d) => d.appointment_date && workNotes?.get(d.dispatch))
        .map((d) => ({ dispatch: d.dispatch, text: `${workNotes!.get(d.dispatch)}\n${d.notes ?? ""}`.slice(0, 1400) }))
        .filter((v, i, arr) => arr.findIndex((x) => x.dispatch === v.dispatch) === i),
    [disp, workNotes],
  );
  const { data: aiTitles } = useVisitSummaries(custno, summaryItems);

  const [filter, setFilter] = useState<Kind | "all">("all");
  // PROPERTY-LINK-01 (Seth 2026-10-16, 300 Lake St W): the house's other ESC
  // accounts with visits — read here under "This house", the default when
  // the house has a past this account doesn't.
  const house = useHouseVisitHistory(custno, locno ?? null);
  const [scope, setScope] = useState<"account" | "house">("house");
  const houseOthers = scope === "house" ? house.others : [];
  const houseVisitCount = house.others.reduce((n, b) => n + b.rows.length, 0);
  // PROP-STORY-01: the era band's pick — show only the previous owner's era
  // (and its archived-notes panel) or only the current owner's.
  const [eraPick, setEraPick] = useState<"prior" | "current" | null>(null);
  const [shown, setShown] = useState(PAGE);
  const [peek, setPeek] = useState<string | null>(null);
  const [peekInvoice, setPeekInvoice] = useState<string | null>(null);
  // FILES-04: clicking a file event opens the preview modal.
  const [previewFile, setPreviewFile] = useState<PegasusFile | null>(null);
  const filesById = useMemo(
    () => new Map((files ?? []).map((f) => [f.id, f])),
    [files],
  );

  const allEvents = useMemo(() => {
    const evs: Ev[] = [];

    // One service event per DISPATCH (multi-day installs consolidate: first
    // block anchors, extra techs/days fold into the subtitle).
    const seenDispatch = new Map<string, { techs: Set<string>; blocks: number }>();
    for (const d of disp ?? []) {
      if (!d.appointment_date) continue;
      const seen = seenDispatch.get(d.dispatch);
      if (seen) {
        if (d.tech_name) seen.techs.add(d.tech_name);
        seen.blocks += 1;
        continue;
      }
      seenDispatch.set(d.dispatch, {
        techs: new Set(d.tech_name ? [d.tech_name] : []),
        blocks: 1,
      });
      const work = workNotes?.get(d.dispatch) ?? null;
      evs.push({
        key: `d-${d.dispatch}`,
        date: d.appointment_date,
        kind: "service",
        // Headline: the AI's findable title when ready, else the rule-based
        // read (tune-up vs repair, plan, equipment + notable action).
        title: aiTitles?.get(d.dispatch) || (work ? summarizeVisit(work, d.notes ?? "") : (extractJobPreview(d.notes) || "Service visit")),
        // Status only when it's NOT the normal completed end-state.
        subtitle:
          [d.tech_name, isDoneStatus(d.status) ? null : d.status]
            .filter(Boolean)
            .join(" · ") || null,
        peekDispatch: d.dispatch,
        dispatch: d.dispatch,
        notes: d.notes,
        workNotes: work,
        prior: era.priorOwnerFor(d.appointment_date),
        equipment: visitEquipmentFor(visitEq, [d.dispatch]),
        locno: d.locno ?? null,
      });
    }
    // Enrich consolidated subtitles ("· 2 more techs / · 9 days").
    for (const e of evs) {
      if (e.kind !== "service" || !e.dispatch) continue;
      const seen = seenDispatch.get(e.dispatch);
      if (!seen) continue;
      const extraTechs = seen.techs.size - 1;
      if (seen.blocks > 1) {
        e.subtitle = `${e.subtitle ?? ""}${extraTechs > 0 ? ` · +${extraTechs} more tech${extraTechs > 1 ? "s" : ""}` : ""} · ${seen.blocks} scheduled blocks`;
      }
    }
    for (const i of inv ?? []) {
      if (!i.inv_date) continue;
      evs.push({
        key: `i-${i.invoice}`,
        date: i.inv_date,
        kind: "invoice",
        title: `Invoice #${i.invoice}${i.inv_amount != null ? ` · ${money(i.inv_amount)}` : ""}`,
        // "Normal · 03" said nothing — only NON-normal types (Quote, Credit
        // Memo) + the department NAME earn the line.
        subtitle:
          [
            i.inv_type && i.inv_type.toLowerCase() !== "normal" ? i.inv_type : null,
            i.dept ? deptLabel(i.dept) : null,
          ]
            .filter(Boolean)
            .join(" · ") || null,
        peekInvoiceId: i.invoice,
        dispatch: i.dispatch,
        prior: era.priorOwnerFor(i.inv_date),
        locno: i.locno ?? null,
      });
    }
    for (const m of msgs ?? []) {
      const word = CHANNEL_WORD[m.channel] ?? "Message";
      evs.push({
        key: `m-${m.id}`,
        date: m.created_at,
        kind: "message",
        title: m.subject || m.body.slice(0, 80) + (m.body.length > 80 ? "…" : ""),
        subtitle:
          m.channel === "note"
            ? `Internal note · ${m.logged_by ?? ""}`
            : `${word} ${m.direction === "outbound" ? "to" : "from"} ${m.endpoint ?? "customer"}`,
        to: `/customers/${custno}?tab=messages`,
        dispatch: m.esc_dispatch,
        locno: m.locno ?? null,
      });
    }
    for (const f of files ?? []) {
      evs.push({
        key: `f-${f.id}`,
        date: f.created_at,
        kind: "file",
        title: f.filename,
        subtitle: `File added · ${f.uploaded_by ?? "—"}`,
        fileId: f.id,
        dispatch: f.dispatch,
      });
    }
    for (const p of proposals ?? []) {
      evs.push({
        key: `p-${p.proposal_id}`,
        date: p.latest_at,
        kind: "proposal",
        locno: (p as { locno?: string | null }).locno ?? null,
        title: p.title ?? `Proposal ${p.proposal_id}`,
        subtitle: `${p.latest_event}${p.tier_selected ? ` · picked ${p.tier_selected}` : ""}${p.amount != null ? ` · ${money(p.amount)}` : ""}`,
        to: `/customers/${custno}?tab=quotes`,
      });
    }
    for (const r of recs ?? []) {
      evs.push({
        key: `r-${r.id}`,
        date: r.created_at,
        kind: "recommendation",
        title: `Recommended: ${r.title}`,
        subtitle: `${r.status}${r.est_price != null ? ` · ${money(r.est_price)}` : ""}`,
      });
    }
    for (const f of extras?.followups ?? []) {
      evs.push({
        key: `fu-${f.id}`,
        date: f.created_at,
        kind: "followup",
        title: f.title,
        subtitle: `${f.source ?? "manual"} · ${f.status}${f.due_date ? ` · due ${f.due_date}` : ""}`,
      });
    }
    for (const t of extras?.tickets ?? []) {
      evs.push({
        key: `t-${t.id}`,
        date: t.created_at,
        kind: "ticket",
        title: t.summary || `${t.lane_key ?? "queue"} ticket`,
        subtitle: [t.source, t.status, t.resolved_by ? `cleared by ${t.resolved_by}` : null]
          .filter(Boolean)
          .join(" · ") || null,
      });
    }
    // PROP-SCOPE-01: a record with no locno of its own inherits its
    // dispatch's property (a photo or text pinned to a Jersey Ave visit IS
    // Jersey Ave history).
    const dispLoc = new Map<string, string | null>();
    for (const d of disp ?? []) if (d.dispatch) dispLoc.set(d.dispatch, d.locno ?? null);
    for (const e of evs) {
      if (e.locno == null && e.dispatch) e.locno = dispLoc.get(e.dispatch) ?? null;
    }
    // PROP-STORY-01: the handoff renders as a divider at the exact boundary,
    // not as a feed event; the era chips above the feed carry the story.
    return evs.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disp, inv, msgs, files, proposals, recs, extras, workNotes, aiTitles, visitEq, era.handoffDate, era.priorOwnerName, era.currentOwnerName, custno]);

  // PROP-SCOPE-01 (Seth 2026-10-09, R&D Batteries: "it should just be showing
  // the selected address history right?"): on a multi-property account the
  // timeline follows the SELECTED property, exactly like the Messages tab —
  // records from this property plus account-wide items (texts with no
  // property on file); one click widens to everything.
  const [showAllProps, setShowAllProps] = useState(false);
  const propScoped = !!multiProperty && !!locno && !showAllProps;
  const scopedEvents = useMemo(
    () => (multiProperty && locno ? allEvents.filter((e) => !e.locno || e.locno === locno) : allEvents),
    [allEvents, multiProperty, locno],
  );
  const events = propScoped ? scopedEvents : allEvents;
  const otherPropCount = allEvents.length - scopedEvents.length;

  // Counts per kind for the filter chips (only kinds that exist show).
  const counts = useMemo(() => {
    const c = new Map<Kind, number>();
    for (const e of events) c.set(e.kind, (c.get(e.kind) ?? 0) + 1);
    return c;
  }, [events]);

  // VISIT BLOCKS (Seth 2026-06-11: "block together the connected stuff by
  // visit"): events sharing a dispatch cluster under the service visit —
  // the invoice, photos and texts of a job read as ONE thing. Grouping only
  // applies to the All view; a filtered view is already a flat answer.
  const stream = useMemo<StreamItem[]>(() => {
    if (filter !== "all") {
      return events
        .filter((e) => e.kind === filter)
        .map((e) => ({ type: "single" as const, date: e.date, ev: e }));
    }
    const byDispatch = new Map<string, Ev[]>();
    const loose: Ev[] = [];
    for (const e of events) {
      if (e.dispatch) {
        const list = byDispatch.get(e.dispatch);
        if (list) list.push(e);
        else byDispatch.set(e.dispatch, [e]);
      } else loose.push(e);
    }
    const items: StreamItem[] = loose.map((e) => ({ type: "single", date: e.date, ev: e }));
    for (const [, list] of byDispatch) {
      const anchor = list.find((e) => e.kind === "service") ?? list[0];
      const children = list.filter((e) => e !== anchor);
      if (children.length === 0) {
        items.push({ type: "single", date: anchor.date, ev: anchor });
      } else {
        items.push({ type: "block", date: anchor.date, anchor, children });
      }
    }
    return items.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  }, [events, filter]);

  // PROP-STORY-01: era math — which side of the handoff an item sits on, the
  // per-era event counts for the band, and the era-filtered stream.
  const handoffISO = era.hasTransfer ? era.handoffDate : null;
  const eraOf = (d: string): "prior" | "current" => (handoffISO && d < handoffISO ? "prior" : "current");
  const eraCounts = useMemo(() => {
    let prior = 0, current = 0;
    if (handoffISO) for (const e of events) (e.date < handoffISO ? prior++ : current++);
    return { prior, current };
  }, [events, handoffISO]);
  const eraStream = useMemo(
    () => (eraPick && handoffISO ? stream.filter((it) => eraOf(it.date) === eraPick) : stream),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stream, eraPick, handoffISO],
  );

  const visible = eraStream.slice(0, shown);
  const totalShownLabel = filter === "all" && !eraPick ? events.length : eraStream.length;

  // ?visit=<dispatch> (the Last Visit tile, the peek's History jump): ring that
  // visit, scroll it into view and open its peek — once per arrival (the
  // location key changes on every navigation, so clicking the tile again
  // re-opens it even when the URL reads the same). Waits for the rows to land.
  const [searchParams, setSearchParams] = useSearchParams();
  const { key: navKey } = useLocation();
  const focusVisit = searchParams.get("visit");
  const [spot, setSpot] = useState<string | null>(null);
  const handledArrival = useRef<string | null>(null);
  useEffect(() => {
    if (!focusVisit) return;
    const arrival = `${navKey}:${focusVisit}`;
    if (handledArrival.current === arrival) return;
    const idx = eraStream.findIndex((it) =>
      it.type === "block" ? it.anchor.dispatch === focusVisit : it.ev.kind === "service" && it.ev.dispatch === focusVisit,
    );
    if (idx < 0) return;
    handledArrival.current = arrival;
    if (idx >= shown) setShown(Math.ceil((idx + 1) / PAGE) * PAGE);
    setSpot(focusVisit);
    setPeek(focusVisit);
  }, [focusVisit, navKey, eraStream, shown]);
  // Scroll happens after the row is in the DOM (it may only appear once
  // `shown` grows); once per spotted visit.
  const scrolledTo = useRef<string | null>(null);
  useEffect(() => {
    if (!spot || scrolledTo.current === spot) return;
    const el = document.getElementById(`visit-${spot}`);
    if (!el) return;
    scrolledTo.current = spot;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [spot, shown, visible.length]);
  const closePeek = () => {
    setPeek(null);
    // Leave the URL clean so a refresh or a back-step doesn't re-open it.
    if (searchParams.has("visit"))
      setSearchParams(
        (prev) => {
          prev.delete("visit");
          return prev;
        },
        { replace: true },
      );
  };

  if (events.length === 0 && houseOthers.length === 0) {
    // TAB-SPEED-01: while the first rows are still on their way this used to
    // flash "No activity on record yet." — say nothing until we know.
    if (dispLoading || invLoading || house.isLoading) {
      return (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="h-14 animate-pulse bg-slate-100" />
          ))}
        </div>
      );
    }
    return (
      <Card className="p-10 text-center text-sm text-slate-500">
        No activity on record yet.
      </Card>
    );
  }

  return (
    <>
      <Card className="p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <HistoryIcon className="h-4 w-4 text-slate-400" />
          <h3 className="text-sm font-semibold text-slate-900">Activity timeline</h3>
          {multiProperty && locno ? (
            <button
              type="button"
              onClick={() => setShowAllProps((v) => !v)}
              className="text-[11px] font-medium text-brand-600 underline-offset-2 transition-colors hover:text-brand-700 hover:underline"
            >
              {propScoped
                ? otherPropCount > 0
                  ? `Show ${otherPropCount} from other properties`
                  : "Showing this property"
                : "Only this property"}
            </button>
          ) : (
            <AllPropertiesChip show={!!multiProperty} />
          )}
          <span className="text-xs text-slate-400">
            {totalShownLabel}{filter !== "all" ? ` of ${events.length}` : ""} events
            {scope === "house" && houseVisitCount > 0 ? ` · ${houseVisitCount} earlier visit${houseVisitCount === 1 ? "" : "s"} to this house` : ""}
          </span>
          {house.others.length > 0 && (
            <span className="inline-flex rounded-full bg-slate-100 p-0.5" role="group" aria-label="Scope">
              {(["account", "house"] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setScope(k)}
                  className={cn("rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition-colors", scope === k ? "bg-slate-900 text-white shadow-sm" : "text-slate-500 hover:text-slate-800")}
                >
                  {k === "account" ? "This account" : "This house"}
                </button>
              ))}
            </span>
          )}
          {/* Type chips — small fixed set, counts shown, one-click filter. */}
          <span className="ml-auto flex flex-wrap items-center gap-1">
            <FilterChip active={filter === "all"} onClick={() => { setFilter("all"); setShown(PAGE); }}>
              All
            </FilterChip>
            {(Object.keys(META) as Kind[])
              .filter((k) => (counts.get(k) ?? 0) > 0)
              .map((k) => (
                <FilterChip
                  key={k}
                  active={filter === k}
                  onClick={() => { setFilter(filter === k ? "all" : k); setShown(PAGE); }}
                >
                  {META[k].label} {counts.get(k)}
                </FilterChip>
              ))}
          </span>
        </div>

        {/* NAV-360-01 / Exhibit B (Seth approved the mock): the house's
            chapters as a full segmented control — avatar cards, raised-white
            selection, and a quiet time-track underneath so tenure shows
            without length crowning anyone "the main person". */}
        {handoffISO && era.priorOwnerName && (
          <EraControl
            priorName={era.priorOwnerName}
            currentName={era.currentOwnerName ?? customerName ?? "Current owner"}
            handoffISO={handoffISO}
            firstISO={allEvents.length ? allEvents[allEvents.length - 1].date : null}
            counts={eraCounts}
            value={eraPick}
            onChange={(v) => { setEraPick(v); setShown(PAGE); }}
          />
        )}

        {/* The previous owner's chapter opens with their archived record. */}
        {eraPick === "prior" && handoffISO && era.priorOwnerName && (
          <PriorEraPanel custno={custno} locno={locno ?? null} priorName={era.priorOwnerName} handoff={handoffISO} />
        )}

        <ol className="space-y-1.5">
          {visible.map((item, idx) => {
            // YEAR separators — the date is the spine of the story
            // (Seth 2026-06-11: "the date should be much more prominent").
            const year = item.date.slice(0, 4);
            const prevYear = idx > 0 ? visible[idx - 1].date.slice(0, 4) : null;
            // PROP-STORY-01: the handoff boundary — a full divider the moment
            // the feed crosses from the current owner's era into the previous
            // owner's (every filtered view keeps it).
            const crossesHandoff =
              !!handoffISO && !eraPick && idx > 0 &&
              visible[idx - 1].date >= handoffISO && item.date < handoffISO;
            const eraSep = crossesHandoff ? (
              <li
                key={`era-${idx}`}
                className="flex items-center gap-1.5 rounded-md bg-slate-50/70 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400"
              >
                <Home className="h-3 w-3" />
                Changed hands {format(parseISO(handoffISO!), "MMM yyyy")} · below is {era.priorOwnerName}&apos;s era
              </li>
            ) : null;
            const sep =
              year !== prevYear ? (
                <li
                  key={`y-${year}-${idx}`}
                  className="flex items-center gap-3 pt-2 first:pt-0"
                >
                  <span className="text-base font-bold tracking-tight text-slate-700">
                    {year}
                  </span>
                  <span className="h-px flex-1 bg-slate-200" />
                </li>
              ) : null;

            if (item.type === "single") {
              return (
                <Fragment key={item.ev.key}>
                  {eraSep}
                  {sep}
                  <EventRow
                    e={item.ev}
                    onPeek={setPeek}
                    onFile={(id) => setPreviewFile(filesById.get(id) ?? null)}
                    onPeekInvoice={setPeekInvoice}
                    spotted={item.ev.kind === "service" && !!spot && item.ev.dispatch === spot}
                  />
                </Fragment>
              );
            }

            // Block glance chips: what the visit BILLED + what's attached.
            const billed = item.children
              .filter((c) => c.kind === "invoice")
              .reduce((s, c) => {
                const m = c.title.match(/\$([\d,]+)/);
                return s + (m ? Number(m[1].replace(/,/g, "")) : 0);
              }, 0);
            const fileCount = item.children.filter((c) => c.kind === "file").length;
            const spotted = !!spot && item.anchor.dispatch === spot;
            return (
              <Fragment key={`blk-${item.anchor.key}`}>
                {eraSep}
                {sep}
                <li className="relative" id={item.anchor.dispatch ? `visit-${item.anchor.dispatch}` : undefined}>
                  {/* VISIT BLOCK — the job + everything attached, one cluster. */}
                  <div
                    className={cn(
                      "-mx-2 rounded-xl border px-2 py-1 transition-colors",
                      spotted ? "border-brand-300 bg-brand-50/50 ring-2 ring-brand-200" : "border-slate-200/80 bg-slate-50/40",
                    )}
                  >
                    <EventRow
                      e={item.anchor}
                      onPeek={setPeek}
                      onFile={(id) => setPreviewFile(filesById.get(id) ?? null)}
                      onPeekInvoice={setPeekInvoice}
                      bare
                      chips={
                        <>
                          {billed > 0 && (
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold tabular-nums text-emerald-700">
                              ${billed.toLocaleString()}
                            </span>
                          )}
                          {fileCount > 0 && (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                              {fileCount} file{fileCount > 1 ? "s" : ""}
                            </span>
                          )}
                        </>
                      }
                    />
                    <ul className="mb-1 ml-[60px] space-y-px border-l border-slate-200 pl-3">
                      {item.children.map((c) => {
                        const cm = META[c.kind];
                        const CIcon = cm.icon;
                        const inner = (
                          <>
                            <CIcon className={cn("h-3.5 w-3.5 shrink-0", cm.tint.split(" ")[1])} />
                            <span className="min-w-0 flex-1 truncate text-xs text-slate-700">
                              {c.title}
                            </span>
                            {c.subtitle && (
                              <span className="hidden shrink-0 truncate text-[11px] text-slate-400 sm:inline">
                                {c.subtitle}
                              </span>
                            )}
                          </>
                        );
                        return (
                          <li key={c.key}>
                            {c.fileId ? (
                              <button
                                type="button"
                                onClick={() => setPreviewFile(filesById.get(c.fileId!) ?? null)}
                                title="Open the file"
                                className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-white"
                              >
                                {inner}
                              </button>
                            ) : c.peekInvoiceId ? (
                              <button
                                type="button"
                                onClick={() => setPeekInvoice(c.peekInvoiceId!)}
                                title="Peek the invoice"
                                className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-white"
                              >
                                {inner}
                              </button>
                            ) : c.to ? (
                              <Link
                                to={c.to}
                                className="flex items-center gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-white"
                              >
                                {inner}
                              </Link>
                            ) : (
                              <div className="flex items-center gap-2 px-1.5 py-1">{inner}</div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </li>
              </Fragment>
            );
          })}
        </ol>

        {eraStream.length > shown && (
          <button
            type="button"
            onClick={() => setShown((n) => n + PAGE)}
            className="mt-2 block w-full rounded-md py-2 text-center text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50/40 hover:text-brand-700"
          >
            Show {Math.min(PAGE, eraStream.length - shown)} more ({eraStream.length - shown} older)
          </button>
        )}
        {houseOthers.map((b) => (
          <HouseEraSection key={`${b.account.custno}/${b.account.locno ?? ""}`} bundle={b} onPeek={setPeek} />
        ))}
        {houseOthers.length > 0 && (
          <p className="mt-3 text-[11px] text-slate-400">Another owner&apos;s visits show what was done at this house. Their prices stay muted and are never quoted to the current owner.</p>
        )}
      </Card>
      <DispatchPeek dispatchId={peek} onClose={closePeek} historyJump={false} />
      <InvoicePeek invoice={peekInvoice} onClose={() => setPeekInvoice(null)} />
      <FilePreviewModal
        file={previewFile}
        custno={custno}
        onClose={() => setPreviewFile(null)}
      />
    </>
  );
}

/** PROPERTY-LINK-01: one other account's visits at this house — an era
 *  divider ("Before this owner · The Landschute Group · builder / company ·
 *  2023–24 · Their account →") and its visits, date-first like every row
 *  here, the write-ups one click away, the money muted ("builder paid"). */
function HouseEraSection({ bundle, onPeek }: { bundle: HouseVisitBundle; onPeek: (dispatch: string) => void }) {
  const navigate = useNavigate();
  const a: HouseAccount = bundle.account;
  const [open, setOpen] = useState<string | null>(null);
  const money = (n: number) => (n === 0 ? "covered" : `$${Math.round(n).toLocaleString()}`);
  return (
    <div className="mt-4">
      <div className="flex items-center gap-2 rounded-md bg-slate-50/70 px-3 py-1.5 text-[11px] font-semibold text-slate-500">
        <Home className="h-3 w-3 shrink-0" />
        <span className="uppercase tracking-wider">{[relationWord(a.relation), houseEraLabel(a)].filter(Boolean).join(" · ")}</span>
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => navigate(`/customers/${a.custno}${a.locno ? `?loc=${a.locno}` : ""}`)}
          className="font-semibold text-brand-600 underline-offset-2 hover:underline"
        >
          Their account →
        </button>
      </div>
      <ol className="mt-1.5 space-y-1.5">
        {bundle.rows.map((v) => {
          const key = `${v.source}-${v.invoice}`;
          const on = open === key;
          const d = v.date ? parseISO(v.date) : null;
          const headline = (v.workNote || v.note || "Service visit").split(/\n/)[0].replace(/^tech notes?:\s*/i, "").trim();
          return (
            <li key={key} className="flex items-start gap-3">
              <span className="w-[48px] shrink-0 rounded-lg border border-slate-200 bg-white py-1 text-center leading-none">
                <span className="block text-[9px] font-bold uppercase tracking-wider text-slate-400">{d ? format(d, "MMM") : "—"}</span>
                <span className="block text-[17px] font-bold text-slate-700">{d ? format(d, "d") : ""}</span>
                <span className="block text-[9px] font-semibold text-slate-400">{d ? format(d, "yyyy") : ""}</span>
              </span>
              <div className="min-w-0 flex-1">
                <button type="button" onClick={() => setOpen(on ? null : key)} className="block w-full text-left">
                  <span className="block truncate text-sm font-medium text-slate-900">{headline.length > 110 ? `${headline.slice(0, 109)}…` : headline}</span>
                  <span className="block truncate text-[11px] text-slate-400">
                    {[v.tech, v.dispatch ? `call #${v.dispatch}` : null, `inv ${v.invoice}`].filter(Boolean).join(" · ")}
                    {" · "}<span title="Another owner's invoice — not this customer's price">{money(v.amount)} — {paidByLabel(a)}</span>
                  </span>
                </button>
                {on && (
                  <div className="mt-1.5 space-y-1.5">
                    {v.note && (
                      <div className="rounded-lg bg-slate-50 px-3 py-2">
                        <div className="text-[11px] font-semibold text-slate-500">The call</div>
                        <p className="mt-0.5 whitespace-pre-line text-xs leading-relaxed text-slate-600">{v.note}</p>
                      </div>
                    )}
                    {v.workNote && (
                      <div className="rounded-lg bg-emerald-50/60 px-3 py-2">
                        <div className="text-[11px] font-semibold text-emerald-600">What we did</div>
                        <p className="mt-0.5 whitespace-pre-line text-xs leading-relaxed text-slate-700">{v.workNote}</p>
                      </div>
                    )}
                    {v.dispatch && (
                      <button type="button" onClick={() => onPeek(v.dispatch!)} className="text-[11px] font-medium text-brand-600 underline-offset-2 hover:underline">
                        Open the visit
                      </button>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** One timeline row — DATE-FIRST (Seth 2026-06-11): a calendar leaf anchors
 *  every row, the small kind-icon sits beside it. `bare` = visit-block anchor
 *  (the block supplies its own frame); `chips` = right-side glance badges. */
function EventRow({
  e,
  onPeek,
  onFile,
  onPeekInvoice,
  bare,
  chips,
  spotted,
}: {
  e: Ev;
  onPeek: (dispatch: string) => void;
  onFile?: (fileId: string) => void;
  onPeekInvoice?: (invoice: string) => void;
  bare?: boolean;
  chips?: React.ReactNode;
  /** The visit the page was sent to (?visit=) — ringed so the eye lands on it. */
  spotted?: boolean;
}) {
  const m = META[e.kind];
  const Icon = m.icon;
  let mon = "";
  let day = "";
  try {
    const d = parseISO(e.date);
    mon = format(d, "MMM");
    day = format(d, "d");
  } catch {
    mon = e.date.slice(5, 7);
    day = e.date.slice(8, 10);
  }
  const body = (
    <>
      {/* The calendar leaf — the date IS the headline of history. */}
      <span className="flex w-11 shrink-0 flex-col items-center rounded-lg bg-white py-1 shadow-e1 ring-1 ring-slate-100">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          {mon}
        </span>
        <span className="-mt-0.5 text-lg font-bold leading-tight tabular-nums text-slate-800">
          {day}
        </span>
      </span>
      <span
        className={cn(
          "mt-1.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
          m.tint,
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1 pt-0.5">
        <span className="block truncate text-sm font-medium text-slate-800">
          {e.title}
          {e.prior && <PriorOwnerBadge name={e.prior} className="ml-1.5 align-middle" />}
        </span>
        {e.subtitle && <span className="block truncate text-xs text-slate-500">{e.subtitle}</span>}
        {/* HIST-EQUIP-01: the unit(s) — only when the record actually says
            which (the peek carries the honest "not tagged" line). */}
        {e.equipment && (
          <VisitEquipmentBlock source="esc" equipment={e.equipment} className="mt-1.5 max-w-xl" />
        )}
        {e.workNotes && e.workNotes.trim() ? (
          <>
            {/* What actually happened — the tech's invoice write-up, in full. */}
            <span className="mt-1 block whitespace-pre-line text-xs leading-relaxed text-slate-700">
              {e.workNotes.trim()}
            </span>
            {/* Then the full dispatch / scheduling notes below, de-emphasized. */}
            {e.notes && e.notes.trim() && (
              <span className="mt-1.5 block whitespace-pre-line text-[11px] leading-snug text-slate-400">
                {e.notes.trim()}
              </span>
            )}
          </>
        ) : (
          e.notes && e.notes.trim() && (
            <span className="mt-1 block whitespace-pre-line text-xs leading-snug text-slate-500">
              {e.notes.trim()}
            </span>
          )
        )}
      </div>
      {chips && <span className="flex shrink-0 items-center gap-1 pt-1">{chips}</span>}
      {(e.to || e.peekDispatch || e.fileId || e.peekInvoiceId) && (
        <ChevronRight className="mt-2 h-4 w-4 shrink-0 text-slate-300" />
      )}
    </>
  );
  const rowCls = cn(
    bare
      ? "flex w-full items-start gap-2.5 rounded-lg px-0 py-1.5 text-left transition-colors hover:bg-white"
      : "-mx-2 flex w-[calc(100%+16px)] items-start gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-slate-50",
    spotted && !bare && "bg-brand-50/50 ring-2 ring-brand-200",
  );
  const content = e.fileId && onFile ? (
    // FILES-04: file rows open the preview modal.
    <button
      type="button"
      onClick={() => onFile(e.fileId!)}
      title="Open the file"
      className={rowCls}
    >
      {body}
    </button>
  ) : e.peekDispatch ? (
    // FLOW-01: dispatch-touching rows PEEK — the working window stays put.
    <button
      type="button"
      onClick={() => onPeek(e.peekDispatch!)}
      title="Preview the dispatch without leaving this page"
      className={rowCls}
    >
      {body}
    </button>
  ) : e.peekInvoiceId && onPeekInvoice ? (
    // Invoice rows PEEK the invoice in place; the peek links to the full one.
    <button
      type="button"
      onClick={() => onPeekInvoice(e.peekInvoiceId!)}
      title="Preview the invoice without leaving this page"
      className={rowCls}
    >
      {body}
    </button>
  ) : e.to ? (
    <Link to={e.to} className={cn(rowCls, "w-auto")}>
      {body}
    </Link>
  ) : (
    <div className={cn("flex items-start gap-2.5 py-1.5")}>{body}</div>
  );
  return bare ? content : (
    <li className="relative" id={e.kind === "service" && e.dispatch ? `visit-${e.dispatch}` : undefined}>
      {content}
    </li>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors",
        active
          ? "bg-brand-700 text-white"
          : "bg-slate-100 text-slate-600 hover:bg-slate-200",
      )}
    >
      {children}
    </button>
  );
}


// ============================================================================
// PROP-STORY-01 — the previous owner's chapter header: their archived
// preferences (NOTE-ERA-02 moved them here) + the dated ESC note lines from
// before the handoff, split out of the one blob so the old customer's story
// is readable WITHOUT tainting the current owner's record.
// ============================================================================
const NOTE_DATE_RE = /(\d{1,2})\/(\d{1,2})\/(\d{4})/;
function noteDateISO(line: string): string | null {
  const m = NOTE_DATE_RE.exec(line);
  if (!m) return null;
  const y = Number(m[3]), mo = Number(m[1]), d = Number(m[2]);
  if (y < 1990 || y > 2100 || mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return `${m[3]}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
/** Lines whose era predates the handoff — same rule as the DB-side archiver:
 *  a line belongs to the last date stamp seen above it; undated header lines
 *  inherit the blob's first dated era. */
function priorNoteLines(blob: string, handoff: string): string[] {
  const lines = blob.split(/\r\n|\r|\n/).map((l) => l.trim()).filter(Boolean);
  let first: string | null = null;
  for (const l of lines) { const d = noteDateISO(l); if (d) { first = d; break; } }
  const out: string[] = [];
  let last: string | null = null;
  for (const l of lines) {
    const d = noteDateISO(l);
    if (d) last = d;
    const eff = d ?? last ?? first;
    if (eff && eff < handoff) out.push(l);
  }
  return out;
}

function PriorEraPanel({ custno, locno, priorName, handoff }: {
  custno: string; locno: string | null; priorName: string; handoff: string;
}) {
  const { data: signals } = useCustomerSignals(custno);
  const { data: notes } = useQuery({
    queryKey: ["prior_era_notes", custno, locno],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string> => {
      let q = supabase.from("mirror_location").select("locno, notes").eq("custno", custno).order("locno").limit(5);
      if (locno) q = q.eq("locno", locno);
      const { data } = await q;
      return (((data ?? []) as { notes: string | null }[]).map((r) => r.notes).find(Boolean) ?? "");
    },
  });
  const archived = (signals ?? []).filter((s) => s.archived_at && !!s.value);
  const lines = useMemo(() => priorNoteLines(notes ?? "", handoff), [notes, handoff]);
  if (archived.length === 0 && lines.length === 0) {
    return (
      <div className="mb-3 rounded-lg border border-hairline bg-slate-50/60 px-3 py-2 text-xs text-slate-500">
        Nothing on file from {priorName}&apos;s era beyond the records below.
      </div>
    );
  }
  return (
    <div className="mb-3 overflow-hidden rounded-xl border border-hairline bg-white">
      <div className="flex flex-wrap items-center gap-2 border-b border-hairline/70 px-3 py-2">
        <Home className="h-3.5 w-3.5 text-slate-400" />
        <span className="text-xs font-semibold text-slate-700">{priorName}&apos;s era — notes &amp; preferences</span>
        <span className="rounded bg-amber-50 px-1.5 py-px text-[10px] font-medium text-amber-700">previous owner</span>
        <span className="ml-auto text-[10.5px] text-slate-400">kept for the record · never the current owner&apos;s wishes</span>
      </div>
      {archived.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-3 py-2">
          {archived.map((s) => (
            <span key={s.id} className="inline-flex max-w-full items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600 ring-1 ring-hairline" title={s.source_text ?? undefined}>
              <b className="shrink-0 capitalize text-slate-500">{s.signal_type.replace(/_/g, " ")}:</b>
              <span className="truncate">{s.value}</span>
            </span>
          ))}
        </div>
      )}
      {lines.length > 0 && (
        <ul className="max-h-52 space-y-1 overflow-y-auto border-t border-hairline/70 px-3 py-2">
          {lines.map((l, i) => (
            <li key={i} className="text-[11.5px] leading-snug text-slate-500">{l}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ============================================================================
// NAV-360-01 / Exhibit B — the owner-era segmented control. Same component
// weight as the tab bar: ≥56px avatar segments in a recessed track, the
// selected chapter raised as a white card, a thin tenure track + year labels
// underneath. Current owner leads.
// ============================================================================
function eraInitials(name: string): string {
  const parts = name.replace(/[^A-Za-z ]/g, " ").trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase() || "–";
}
function EraControl({ priorName, currentName, handoffISO, firstISO, counts, value, onChange }: {
  priorName: string; currentName: string; handoffISO: string; firstISO: string | null;
  counts: { prior: number; current: number };
  value: "prior" | "current" | null;
  onChange: (v: "prior" | "current" | null) => void;
}) {
  const handYr = Number(handoffISO.slice(0, 4));
  const firstYr = firstISO ? Number(firstISO.slice(0, 4)) : handYr;
  const nowYr = new Date().getFullYear();
  const priorYrs = Math.max(1, handYr - firstYr);
  const curYrs = Math.max(1, nowYr - handYr || 1);
  const seg = (on: boolean) =>
    cn(
      "flex min-h-[56px] flex-1 cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2 text-left transition-all duration-150 ease-out-quart focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
      on ? "bg-white shadow-e2 ring-1 ring-black/[0.04]" : "hover:bg-white/60",
    );
  const Av = ({ cls, children }: { cls: string; children: React.ReactNode }) => (
    <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-xl text-[12.5px] font-extrabold text-white shadow-e1", cls)}>
      {children}
    </span>
  );
  return (
    <div className="mb-3">
      <div role="tablist" aria-label="Owner era filter" className="flex gap-1.5 rounded-2xl border border-hairline bg-slate-200/40 p-1.5">
        <button type="button" role="tab" aria-selected={value === null} onClick={() => onChange(null)} className={seg(value === null)}>
          <Av cls="bg-gradient-to-br from-slate-500 to-slate-600"><Home className="h-4 w-4" /></Av>
          <span className="min-w-0">
            <span className="block truncate text-[13.5px] font-bold tracking-tight text-slate-800">All history</span>
            <span className={cn("block truncate text-[11.5px]", value === null ? "text-slate-500" : "text-slate-400")}>
              {counts.prior + counts.current} events · {firstYr}–today
            </span>
          </span>
        </button>
        <button type="button" role="tab" aria-selected={value === "current"} onClick={() => onChange(value === "current" ? null : "current")} className={seg(value === "current")}>
          <Av cls="bg-gradient-to-br from-brand-500 to-brand-700">{eraInitials(currentName)}</Av>
          <span className="min-w-0">
            <span className="block truncate text-[13.5px] font-bold tracking-tight text-slate-800">{currentName}</span>
            <span className={cn("block truncate text-[11.5px]", value === "current" ? "text-slate-500" : "text-slate-400")}>
              Since {handYr} · {counts.current} event{counts.current === 1 ? "" : "s"}
            </span>
          </span>
        </button>
        <button type="button" role="tab" aria-selected={value === "prior"} onClick={() => onChange(value === "prior" ? null : "prior")} className={seg(value === "prior")}>
          <Av cls="bg-gradient-to-br from-stone-400 to-stone-500">{eraInitials(priorName)}</Av>
          <span className="min-w-0">
            <span className="block truncate text-[13.5px] font-bold tracking-tight text-slate-800">{priorName}</span>
            <span className={cn("block truncate text-[11.5px]", value === "prior" ? "text-slate-500" : "text-slate-400")}>
              {firstYr}–{handYr} · {counts.prior} event{counts.prior === 1 ? "" : "s"} · previous owner
            </span>
          </span>
        </button>
      </div>
      {/* the tenure track — time, told quietly */}
      <div className="mt-2 flex gap-0.5 px-1" aria-hidden="true">
        <span
          className={cn("h-1.5 rounded-full bg-slate-300/80 transition-opacity", value === "current" && "opacity-30")}
          style={{ flexGrow: priorYrs }}
        />
        <span
          className={cn("h-1.5 rounded-full bg-brand-500 transition-opacity", value === "prior" && "opacity-30")}
          style={{ flexGrow: curYrs }}
        />
      </div>
      <div className="mt-1 flex justify-between px-1 text-[11px] tabular-nums text-slate-400" aria-hidden="true">
        <span>{firstYr}</span>
        <span>sold {format(parseISO(handoffISO), "MMM yyyy")}</span>
        <span>today</span>
      </div>
    </div>
  );
}
